import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Tabs, Tab, IconButton, Typography, CircularProgress, Paper } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TodayIcon from '@mui/icons-material/Today';
import ScheduleIcon from '@mui/icons-material/Schedule';
import EditCalendarIcon from '@mui/icons-material/EditCalendar';
import HistoryIcon from '@mui/icons-material/History';
import FlightIcon from '@mui/icons-material/Flight';
import API_ENDPOINTS from '../config/apiConfig';
import { useTranslation } from '../i18n/i18n';
import { getMonday, addDays, getTodayISO, formatWeekRange } from '../utils/dateUtils';
import TimecardWeekView from './TimecardWeekView';
import TimecardSummary from './TimecardSummary';
import EmployeeScheduleCalendar from './EmployeeScheduleCalendar';
import VacationRequest from './VacationRequest';

export default function TimecardPanel() {
  const { t, lang } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const subTabKeys = ['schedule', 'entry', 'history', 'vacations'];
  const subTabKey = searchParams.get('sub') || 'entry';
  const subTab = subTabKeys.indexOf(subTabKey) >= 0 ? subTabKeys.indexOf(subTabKey) : 0;
  const [weekStart, setWeekStart] = useState(() => getMonday(getTodayISO()));
  const [userEmail, setUserEmail] = useState('');
  const [timecard, setTimecard] = useState(null);
  const [schedule, setSchedule] = useState(null);
  const [vacations, setVacations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const res = await fetch(`${API_ENDPOINTS.BASEURL}/api/user/me`, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          setUserEmail(data.email);
        }
      } catch (err) {
        console.error('Error fetching user:', err);
      }
    };
    fetchUser();
  }, []);

  const fetchData = useCallback(async () => {
    if (!userEmail) return;
    setLoading(true);
    try {
      const [tcRes, schedRes, vacRes] = await Promise.all([
        fetch(`${API_ENDPOINTS.TIMECARD_GET(userEmail)}?week_start=${weekStart}`, { credentials: 'include' }),
        fetch(`${API_ENDPOINTS.SCHEDULE_EFFECTIVE(userEmail)}?week_start=${weekStart}`, { credentials: 'include' }),
        fetch(API_ENDPOINTS.VACATION_GET(userEmail), { credentials: 'include' })
      ]);

      if (tcRes.ok) {
        const tcData = await tcRes.json();
        setTimecard(tcData.timecard || null);
      }

      if (schedRes.ok) {
        const schedData = await schedRes.json();
        setSchedule(schedData.schedule || null);
      } else {
        setSchedule(null);
      }

      if (vacRes.ok) {
        const vacData = await vacRes.json();
        setVacations(vacData.vacations || []);
      }
    } catch (err) {
      console.error('Error fetching timecard data:', err);
    } finally {
      setLoading(false);
    }
  }, [userEmail, weekStart]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const scrollPosRef = useRef(0);

  useEffect(() => {
    if (!loading && scrollPosRef.current > 0) {
      requestAnimationFrame(() => {
        window.scrollTo(0, scrollPosRef.current);
        scrollPosRef.current = 0;
      });
    }
  }, [loading]);

  const saveScrollAndNavigate = (navigateFn) => {
    scrollPosRef.current = window.scrollY;
    navigateFn();
  };

  const goToPreviousWeek = () => saveScrollAndNavigate(() => setWeekStart(addDays(weekStart, -7)));
  const goToNextWeek = () => saveScrollAndNavigate(() => setWeekStart(addDays(weekStart, 7)));
  const goToToday = () => saveScrollAndNavigate(() => setWeekStart(getMonday(getTodayISO())));

  const handleSubTabChange = (_, newValue) => {
    setSearchParams(prev => {
      prev.set('sub', subTabKeys[newValue]);
      return prev;
    });
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 2, sm: 3, md: 4 }, py: { xs: 3, sm: 4 } }}>
      {/* Page Header */}
      <Box sx={{ mb: { xs: 3, sm: 4 } }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          {t('calendar_panel') || 'Timecard'}
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary', maxWidth: 640 }}>
          {t('calendar_subtitle') || 'View your schedule, submit timecards, and manage vacation requests.'}
        </Typography>
      </Box>

      {/* Tabs */}
      <Paper elevation={0} sx={{ borderRadius: 2, border: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={subTab} onChange={handleSubTabChange} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile sx={{ minHeight: 48 }}>
          <Tab icon={<ScheduleIcon />} iconPosition="start" label={t('calendar_tab_schedule')} sx={{ textTransform: 'none', minHeight: 48 }} />
          <Tab icon={<EditCalendarIcon />} iconPosition="start" label={t('calendar_tab_entry')} sx={{ textTransform: 'none', minHeight: 48 }} />
          <Tab icon={<HistoryIcon />} iconPosition="start" label={t('calendar_tab_history')} sx={{ textTransform: 'none', minHeight: 48 }} />
          <Tab icon={<FlightIcon />} iconPosition="start" label={t('vacation_tab')} sx={{ textTransform: 'none', minHeight: 48 }} />
        </Tabs>
      </Paper>

      {subTab === 0 && userEmail && (
        <EmployeeScheduleCalendar userEmail={userEmail} vacations={vacations} />
      )}

      {subTab === 1 && (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 3 }}>
            <IconButton onClick={goToPreviousWeek} size="small">
              <ChevronLeftIcon />
            </IconButton>
            <IconButton onClick={goToToday} size="small" title={t('calendar_nav_thisweek')}>
              <TodayIcon />
            </IconButton>
            <Typography variant="h6" sx={{ minWidth: { xs: 120, sm: 200 }, textAlign: 'center', fontWeight: 600 }}>
              {formatWeekRange(weekStart, lang)}
            </Typography>
            <IconButton onClick={goToNextWeek} size="small">
              <ChevronRightIcon />
            </IconButton>
          </Box>

          <Box sx={{ position: 'relative' }}>
            {loading && (
              <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}>
                <CircularProgress size={20} />
              </Box>
            )}
            <TimecardWeekView
              userEmail={userEmail}
              weekStart={weekStart}
              schedule={schedule}
              timecard={timecard}
              onSaved={fetchData}
              vacations={vacations}
            />
          </Box>
        </Box>
      )}

      {subTab === 2 && userEmail && (
        <TimecardSummary userEmail={userEmail} />
      )}

      {subTab === 3 && userEmail && (
        <VacationRequest userEmail={userEmail} />
      )}
    </Box>
  );
}
